import React, { useState, useEffect, useRef, JSX, useContext } from 'react';
import { Link } from 'react-router-dom';
import { ThemeContext } from '../contexts/ThemeContext'; // <-- Importáljuk a ThemeContext-et
import '../styles/HomePage.css';
import Footer from '../components/Footer';
import TopicIcon from '../components/TopicIcons';

// 🔽 Képek és logó importálása (győződj meg róla, hogy az útvonalak helyesek!)
import LightModeMainLogo from '../assets/images/lightlogo.jpeg';
import DarkModeMainLogo from '../assets/images/darklogo.jpeg';

// Import ALL background images for your topics
import defaultBg from '../assets/images/your-background-image.jpg';
import mobileBg from '../assets/images/mobile-background.jpg'; // <-- ÚJ: Mobil háttérkép
import maganoraBg from '../assets/images/maganora-bg.jpg';
import agykontrollBg from '../assets/images/agykontroll-bg.jpg';
import csillagBg from '../assets/images/csillag-bg.jpg';
import kapcsolatBg from '../assets/images/kapcsolat-bg.jpg';
import tanulasTreningBg from '../assets/images/tanulas-trening-bg.jpg';

interface Topic {
  id: string;
  name: string;
  backgroundImage: string;
  content: JSX.Element;
}

const HomePage: React.FC = () => {
  const [activeTopic, setActiveTopic] = useState<string | null>(null);
  const [isNavbarActive, setIsNavbarActive] = useState(false);
  const topicRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const landingPageRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const { darkMode } = useContext(ThemeContext)!; // <-- A darkMode állapot lekérése

  // 🆕 A fő logó dinamikus kiválasztása a téma alapján
  const currentMainLogo = darkMode ? LightModeMainLogo : DarkModeMainLogo;

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileOrTablet(window.innerWidth <= 992);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const topics: Topic[] = [
    {
      id: 'maganora',
      name: 'Magánóra',
      backgroundImage: maganoraBg,
      content: (
        <>
          <h2>Magánóra</h2>
          <p>Ebben a szekcióban minden információt megtalálsz a magánóráimról. Személyre szabott figyelem, rugalmas időpontok, és a gyermek egyéni fejlődésére fókuszálva.</p>
          <p>Kiemelt területek: olvasás, írás, matematika, problémamegoldó képesség fejlesztése.</p>
          <p>Keress engem bizalommal, hogy megtaláld a tökéletes megoldást gyermeked számára!</p>
          <p><Link to="/letolthetotartalmak" className="more-info-link">A letölthető tartalmakért kattints ide.</Link></p>
          <p><Link to="/maganora" className="more-info-link">További információkért kattints ide.</Link></p>
        </>
      ),
    },
    {
      id: 'agykontroll',
      name: 'Gyermekagykontroll',
      backgroundImage: agykontrollBg,
      content: (
        <>
          <h2>Gyermek-agykontroll Tudnivalók</h2>
          <p>A Gyermek-agykontroll programunk segít a gyerekeknek felfedezni belső erőforrásaikat, javítani a koncentrációjukat, és stresszmentesen tanulni.</p>
          <p>Módszereink játékosak és életkoruknak megfelelők, hogy a tanulás ne csak hatékony, hanem szórakoztató is legyen.</p>
          <p>Tudj meg többet a módszerről és a jelentkezési feltételekről!</p>
          <p><Link to="/letolthetotartalmak" className="more-info-link">A letölthető tartalmakért kattints ide.</Link></p>
          <p><Link to="/gyermekagykontroll" className="more-info-link">További információkért kattints ide.</Link></p>
        </>
      ),
    },
    {
      id: 'tanulas-trening',
      name: 'Tanulási tréning',
      backgroundImage: tanulasTreningBg,
      content: (
        <>
          <h2>Tanulás Tréning: Lásd magadban a csodát!</h2>
          <p>Fedezd fel a benned rejlő képességeket és hozd ki magadból a maximumot tanulási tréningjeink segítségével! Megmutatjuk, hogyan tanulhatsz hatékonyabban, stresszmentesen és örömmel.</p>
          <p>Fejleszd a memóriádat, a koncentrációdat és a problémamegoldó képességedet játékos, interaktív módszerekkel, amelyek a Te egyéni tempódhoz igazodnak.</p>
          <p>Ne csak lásd, hanem éld is meg a benned rejlő csodát!</p>
          <p><Link to="/letolthetotartalmak" className="more-info-link">A letölthető tartalmakért kattints ide.</Link></p>
          <p><Link to="/tanulasitrening" className="more-info-link">További információkért kattints ide.</Link></p>
        </>
      ),
    },
    {
      id: 'csillag',
      name: 'Chillag',
      backgroundImage: csillagBg,
      content: (
        <>
          <h2>Rólunk: A Csillagtanoda Küldetése</h2>
          <p>A Csillagtanoda egy olyan hely, ahol hiszünk minden gyermek egyediségében és abban, hogy a tanulás örömteli utazás lehet.</p>
          <p>Pedagógusaink elhivatottak, és azon dolgoznak, hogy a gyerekek ne csak tudást szerezzenek, hanem önbizalmuk is erősödjön.</p>
          <p>Fedezd fel filozófiánkat és csapatunkat!</p>
          <p><Link to="/chillag" className="more-info-link">További információkért kattints ide.</Link></p>
        </>
      ),
    },
    {
      id: 'kapcsolat',
      name: 'AI',
      backgroundImage: kapcsolatBg,
      content: (
        <>
          <h2>🤖 Érdeklődj a Mesterséges Intelligencia Képzésünkről!</h2>
          <p>Érdekel a jövő technológiája? Szeretnél belevágni a Mesterséges Intelligencia világába? Jelentkezz képzésünkre, vagy kérj további információkat!</p>
          <p>
            <Link to="/ai" className="more-info-link">
              További információkért kattints ide.
            </Link>
          </p>
        </>
      ),
    },
  ];

  const handleButtonClick = (topicId: string) => {
    setActiveTopic(topicId);
    setIsNavbarActive(true);
    setIsMenuOpen(false); // Menü bezárása gombnyomásra

    const NAVBAR_ANIMATION_DURATION = 100;
    const SCROLL_DELAY = NAVBAR_ANIMATION_DURATION + 10;

    setTimeout(() => {
      topicRefs.current[topicId]?.scrollIntoView({ behavior: 'smooth' });
    }, SCROLL_DELAY);
  };

  const handleGoHome = () => {
    setActiveTopic(null);
    setIsNavbarActive(false);
    setIsMenuOpen(false); // Menü bezárása
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const handleScrollForNavbar = () => {
      const landingPageHeight = window.innerHeight;

      if (window.scrollY > landingPageHeight * 0.1) {
        if (!isNavbarActive) {
          setIsNavbarActive(true);
        }
      } else {
        if (isNavbarActive) {
          setIsNavbarActive(false);
          setActiveTopic(null);
          setIsMenuOpen(false); // Menü bezárása, ha visszagörgetünk a tetejére
        }
      }
    };

    window.addEventListener('scroll', handleScrollForNavbar);
    return () => window.removeEventListener('scroll', handleScrollForNavbar);
  }, [isNavbarActive, activeTopic]);

  return (
    <div className="home-page-container">
      {/* Background Layers */}
      <div
        className={`background-layer base-layer ${activeTopic ? 'hidden' : ''}`}
        style={{ backgroundImage: `url(${isMobileOrTablet ? mobileBg : defaultBg})` }}
      ></div>

      {topics.map((topic) => (
        <div
          key={topic.id}
          className={`background-layer topic-bg ${activeTopic === topic.id ? 'active' : ''}`}
          style={{ backgroundImage: `url(${topic.backgroundImage})` }}
        ></div>
      ))}

      {/* Overlay for contrast and dimming */}
      <div className="background-dim-overlay"></div>

      {/* Main content overlay for landing page elements */}
      <div className={`content-overlay ${activeTopic ? 'hidden' : ''}`} ref={landingPageRef}>
        <div className="top-right-content">
          <div className="header-section clickable-header" onClick={handleGoHome}>
            <img src={currentMainLogo} alt="Csillagtanoda Logó" className="star-icon" />
            <h1 className="company-name">CSILLAGTANODA</h1>
          </div>
          <p className="slogan-text">
            Lásd magadban<br />
            a csodát!
          </p>
        </div>

        {/* Initial Button Container */}
        <div className="button-container">
          {topics.map((topic) => (
            <button
              key={topic.id}
              className="nav-button"
              onClick={() => handleButtonClick(topic.id)}
              data-title={topic.name === 'AI' ? 'Mesterséges intelligencia' : undefined}
            >
              <TopicIcon id={topic.id} size={isMobileOrTablet ? 28 : 36} style={{ marginRight: 10 }} />
              {topic.name}
            </button>
          ))}
        </div>
      </div>

      {/* Dynamic Navbar */}
      <nav className={`navbar ${isNavbarActive ? 'active' : ''}`}>
        <div className="navbar-logo clickable-header" onClick={handleGoHome}>
          <img src={currentMainLogo} alt="Csillagtanoda Logó" className="star-icon navbar-star" />
          <span className="company-name navbar-name">CSILLAGTANODA</span>
        </div>

        <div className="navbar-buttons">
          {!isMobileOrTablet && (
            <div className="navbar-buttons-desktop">
              {topics.map((topic) => (
                <button
                  key={`nav-${topic.id}`}
                  className={`nav-item-button ${activeTopic === topic.id ? 'active' : ''}`}
                  onClick={() => handleButtonClick(topic.id)}
                >
                  <TopicIcon id={topic.id} size={24} style={{ marginRight: 8 }} />
                  {topic.name}
                </button>
              ))}
            </div>
          )}

          {/* Mobile/Tablet view: hamburger ikon */}
          {isMobileOrTablet && (
            <div
              className="navbar-hamburger"
              role="button"
              aria-label="Menü megnyitása"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              ☰
            </div>
          )}

          {/* Mobile dropdown menu */}
          {isMenuOpen && isMobileOrTablet && (
            <div className="navbar-dropdown">
              {topics.map((topic) => (
                <button
                  key={`dropdown-${topic.id}`}
                  className={`dropdown-item ${activeTopic === topic.id ? 'active' : ''}`}
                  onClick={() => {
                    handleButtonClick(topic.id);
                  }}
                >
                  <TopicIcon id={topic.id} size={24} style={{ marginRight: 8 }} />
                  {topic.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Topic Sections */}
      <div className="topic-sections-wrapper">
        {topics.map((topic) => (
          <section
            key={topic.id}
            id={topic.id}
            ref={(el: HTMLDivElement | null) => {
              topicRefs.current[topic.id] = el;
            }}
            className="topic-section"
          >
            <div className="topic-content">
              {topic.content}
            </div>
          </section>
        ))}
      </div>
      {/* FOOTER */}
      <Footer />
    </div>
  );
};

export default HomePage;